import React, { useState, useEffect } from 'react';
import { useAuth } from '../../util/authContext';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import Logo from '../../util/Logo';

const FADE_DURATION = 400;

/**
 * Blocks rendering of the app until initialization is complete.
 * Shows a loading screen with progress messages while:
 * - Encryption key is being set up
 * - Local database is initializing
 * - Sync engine is connecting
 *
 * Fades the loading screen out and the app in for a smooth transition.
 * Renders children immediately if user is not authenticated
 * (so sign-in/sign-up pages are accessible without delay).
 */
const AppInitializer = ({ children }) => {
  const { isAuthenticated, isAppReady, initStage } = useAuth();

  // Transition: once ready, fade out the loading screen, then reveal the app
  const [showLoading, setShowLoading] = useState(true);
  const [showApp, setShowApp] = useState(false);

  useEffect(() => {
    if (isAppReady && isAuthenticated) {
      // Start fading out the loading screen
      setShowLoading(false);
      // After the fade-out completes, fade in the app
      const timer = setTimeout(() => setShowApp(true), FADE_DURATION);
      return () => clearTimeout(timer);
    }
  }, [isAppReady, isAuthenticated]);

  // Not authenticated — let routes render (they'll redirect to signin)
  if (!isAuthenticated) {
    return children;
  }

  // Still initializing, or fading out the loading screen
  if (!showApp) {
    return (
      <Fade in={showLoading} timeout={FADE_DURATION}>
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#fafafa',
          }}
        >
          <Box sx={{ width: 200, mb: 5 }}>
            <Logo />
          </Box>
          <Box sx={{ width: 280 }}>
            <LinearProgress
              sx={{
                height: 4,
                borderRadius: 2,
                mb: 2,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                },
              }}
            />
            <Fade in={!!initStage} timeout={300}>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ minHeight: 24 }}
              >
                {initStage}
              </Typography>
            </Fade>
          </Box>
        </Box>
      </Fade>
    );
  }

  // Ready — fade in the app
  return (
    <Fade in timeout={FADE_DURATION}>
      <Box sx={{ minHeight: '100vh' }}>
        {children}
      </Box>
    </Fade>
  );
};

export default AppInitializer;
